import React, { useRef, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_MATERIAL,
  CATALOGS,
  IMPORT_MATERIAL,
  MATERIAL,
} from "../../../constants/route-paths";
import {
  deleteItem,
  editItem as editItemAPI,
  getInventoryLists,
  getInventoryListWithDuplicates,
  listCategories,
} from "../../../actions/inventory.action";
import DeleteDialog from "../../../components/delete-dialog";
import AddCategory from "./add-category";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SwapMaterial from "../../../components/materials/swap-material/index";
import { numberFormat } from "../../../util/currency-formatter";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

import CustomTable from "../../../components/table/table";
import CategoryFilter from "../../../components/table/category-filter";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Cookies from "js-cookie";
import { PerPageCount } from "../../../util/enums";
import DuplicateMaterialPopup from "./DuplicateMaterialPopup";
import MoveCatalogDialog from "../../../components/move-catalog-dialog/move-catalog-dialog";
import MobileMaterialsModal from "./MobileMaterialsModal";
import Modal from "../../../components/modal/modal";
import { getMobileVisibleMaterialsAction, setMobileVisibleMaterialIdsAction } from "../../../actions/mobile-materials.action";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { errorSnackBar, warningSnackBar } from "../../../actions/common.action";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function MaterialList({ writePermission, ...props }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const categoryList = useSelector((state) => state.catalog.catalogs) || [];
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({
    open: false,
    internal: [],
    external: [],
    deleteDialogState: false,
    categoryDialogState: false,
    moveDialogState: false,
    dynamiclookup: {},
    categories: {},
    filter: searchParams.get("id") ? [searchParams.get("id")] : [],
    categoryList: [],
    limit: PerPageCount.DEFAULT,
    currentPage: 1,
    internalCount: 0,
    externalCount: 0,
    showDuplicatesOnly: false,
    isLoading: false,
  });
  const [duplicateButtonLoading, setDuplicateButtonLoading] = useState(false);

  const [offset, setOffset] = useState(0);
  const [activeTab, setActiveTab] = useState("external");
  const [search, setSearch] = useState("");
  const [duplicatePopupOpen, setDuplicatePopupOpen] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [mobileSelectedIds, setMobileSelectedIds] = useState([]);
  const [mobileItems, setMobileItems] = useState([]);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [materialToRemove, setMaterialToRemove] = useState(null);

  /**
   * Refreshes the mobile materials list by fetching full data from backend.
   * Backend now returns complete material details, eliminating individual fetches.
   */
  const refreshMobileMaterials = async () => {
    setMobileLoading(true)
    try {
      const res = await dispatch(getMobileVisibleMaterialsAction())
      const data = res || {}
      const ids = data.ids || []
      const materials = data.materials || []

      setMobileSelectedIds(ids)

      // Transform materials to match table format
      const transformed = materials.map((m, i) => ({
        id: m.id,
        item: m.item,
        partNo: m.part_no,
        manufacturer: m.manufacturer,
        unitLabel: m.unit,
        costValue: m.cost,
        status: m.status,
        categoryId: m.category_id,
        sno: i + 1
      }))

      setMobileItems(transformed)
    } finally {
      setMobileLoading(false)
    }
  }

  /**
   * Handles removal of a material from mobile materials list
   */
  const handleRemoveMobileMaterial = async () => {
    if (!materialToRemove) return

    try {
      const nextIds = (mobileSelectedIds || []).filter(id => id !== materialToRemove.id)
      await dispatch(setMobileVisibleMaterialIdsAction(nextIds))
      setMobileSelectedIds(nextIds)
      setMobileItems(prev => prev.filter(i => i.id !== materialToRemove.id).map((item, index) => ({
        ...item,
        sno: index + 1
      })))
      setRemoveConfirmOpen(false)
      setMaterialToRemove(null)
    } catch (error) {
      dispatch(errorSnackBar(error?.message || 'Failed to remove material'))
      setRemoveConfirmOpen(false)
      setMaterialToRemove(null)
    }
  }

  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef(null);
  const lastCallTimeRef = useRef(0);

  useEffect(() => {
    dispatch(listCategories()).catch(() => {
      dispatch(
        errorSnackBar("Failed to load categories. Please try again later.")
      );
    });
  }, []);

  const getInventoryList = useCallback(
    (categoryIds, type) => {
      const recordsPerPage = Cookies.get("recordsPerPage")
        ? parseInt(Cookies.get("recordsPerPage"))
        : 25;

      const hasCategory = Array.isArray(categoryIds)
        ? categoryIds.length > 0
        : String(categoryIds || "").trim().length > 0;
      const effectiveOffset = offset;

      const params = {
        includeInactive: true,
        findDuplicate: true,
        categoryId: Array.isArray(categoryIds) ? categoryIds.join(',') : String(categoryIds || ''),
        offset: effectiveOffset,
        limit: recordsPerPage,
        search,
        type
      };

      setState((prev) => ({ ...prev, isLoading: true }));

      const apiCall = state.showDuplicatesOnly
        ? dispatch(getInventoryListWithDuplicates(params))
        : dispatch(getInventoryLists(params));
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 30000);
      });

      return Promise.race([apiCall, timeoutPromise])
        .then((inventory) => {
          let internal = [],
            external = [];
          const data = {};
          const allCategories = JSON.parse(
            JSON.stringify(inventory.data?.category || inventory.category || [])
          );
          let sno = Number(
            offset || inventory.data?.offset || inventory.offset
          );

          if (!allCategories || allCategories.length === 0) {
            dispatch(
              warningSnackBar("No materials found for the selected criteria.")
            );
            setState((prev) => ({
              ...prev,
              internal: [],
              external: [],
              internalCount: 0,
              externalCount: 0,
              isLoading: false,
            }));
            setDuplicateButtonLoading(false);
            return;
          }

          let processedCount = 0;
          let skippedCount = 0;

          allCategories.forEach((item, i) => {
            try {
              const { category } = item;

              if (!category) {
                skippedCount++;
                return;
              }

              if (!category.id) {
                skippedCount++;
                return;
              }

              sno += 1;
              item.sno = sno;
              item.categoryId = category.id;
              item.category = category;
              item.costValue = item.cost;
              item.unitLabel = item.unit;
              item.cost = `${numberFormat(item.cost)}/${item.unit}`;
              if (category.vendor_id) external.push(item);
              else internal.push(item);
              processedCount++;
            } catch (itemError) {
              skippedCount++;
            }
          });

          setState((prev) => ({
            ...prev,
            internal,
            external,
            internalCount:
              inventory.data?.internal || inventory.internal || internal.length,
            externalCount:
              inventory.data?.external || inventory.external || external.length,
            limit: inventory.data?.limit || inventory.limit,
            currentPage: inventory.data?.currentPage || inventory.currentPage,
            dynamiclookup: data,
            isLoading: false,
          }));

          setDuplicateButtonLoading(false);

          return inventory;
        })
        .catch((error) => {
          console.error("Error fetching inventory:", error);
          setState((prev) => ({ ...prev, isLoading: false }));
          setDuplicateButtonLoading(false);

          if (state.showDuplicatesOnly) {
            dispatch(
              errorSnackBar(
                "Failed to load duplicate materials. Showing all materials instead."
              )
            );
            setState((prev) => ({ ...prev, showDuplicatesOnly: false }));
            return dispatch(getInventoryLists(params)).then((inventory) => {
              let internal = [],
                external = [];
              const data = {};
              const allCategories = JSON.parse(
                JSON.stringify(
                  inventory.data?.category || inventory.category || []
                )
              );
              let sno = Number(
                offset || inventory.data?.offset || inventory.offset
              );

              if (!allCategories || allCategories.length === 0) {
                dispatch(
                  warningSnackBar("No materials found in fallback response.")
                );
                setState((prev) => ({
                  ...prev,
                  internal: [],
                  external: [],
                  internalCount: 0,
                  externalCount: 0,
                }));
                setDuplicateButtonLoading(false);
                return;
              }

              let processedCount = 0;
              let skippedCount = 0;

              allCategories.forEach((item, i) => {
                try {
                  const { category } = item;

                  if (!category) {
                    skippedCount++;
                    return;
                  }

                  if (!category.id) {
                    skippedCount++;
                    return;
                  }

                  sno += 1;
                  item.sno = sno;
                  item.categoryId = category.id;
                  item.category = category;
                  item.costValue = item.cost;
                  item.unitLabel = item.unit;
                  item.cost = `${numberFormat(item.cost)}/${item.unit}`;
                  if (category.vendor_id) external.push(item);
                  else internal.push(item);
                  processedCount++;
                } catch (itemError) {
                  skippedCount++;
                }
              });

              setState((prev) => ({
                ...prev,
                internal,
                external,
                internalCount:
                  inventory.data?.internal ||
                  inventory.internal ||
                  internal.length,
                externalCount:
                  inventory.data?.external ||
                  inventory.external ||
                  external.length,
                limit: inventory.data?.limit || inventory.limit,
                currentPage:
                  inventory.data?.currentPage || inventory.currentPage,
                dynamiclookup: data,
              }));
              setDuplicateButtonLoading(false);
            });
          } else {
            if (error.message === "Request timeout") {
              dispatch(
                errorSnackBar(
                  "Request timed out. Please try again or reduce the number of items per page."
                )
              );
            } else {
              dispatch(
                errorSnackBar(
                  "Failed to load materials. Please try again later."
                )
              );
            }
          }
        });
    },
    [
      dispatch,
      offset,
      search,
      state.showDuplicatesOnly,
      state.limit,
      state.filter,
      activeTab,
    ]
  );

  useEffect(() => {
    const currentParams = {
      filter: state.filter.join(","),
      activeTab,
      offset,
      search,
      showDuplicatesOnly: state.showDuplicatesOnly,
      limit: Number(state.limit),
    };

    const now = Date.now();

    if (isLoadingRef.current) {
      return;
    }

    if (now - lastCallTimeRef.current < 100) {
      return;
    }

    if (
      lastCallParamsRef.current &&
      JSON.stringify(lastCallParamsRef.current) ===
      JSON.stringify(currentParams)
    ) {
      return;
    }

    isLoadingRef.current = true;
    lastCallParamsRef.current = currentParams;

    getInventoryList(state.filter, activeTab)
      .then((categories) => {
        setState((prev) => ({
          ...prev,
          categories,
        }));
      })
      .catch((e) => {
        dispatch(
          errorSnackBar("Failed to load categories. Please try again later.")
        );
      })
      .finally(() => {
        isLoadingRef.current = false;
        lastCallTimeRef.current = Date.now();
      });
  }, [
    getInventoryList,
    state.filter,
    activeTab,
    offset,
    search,
    state.showDuplicatesOnly,
    state.limit,
  ]);

  const updateOffset = (pageNumber, limit) => {
    const validPageNumber = pageNumber > 0 ? pageNumber : 1;
    if (!limit) return;
    const _offset = (validPageNumber - 1) * limit;
    setOffset(_offset);
    setState((prev) => ({
      ...prev,
      currentPage: validPageNumber,
    }));
  };
  const handleLimitChange = (newLimit) => {
    setState((prevState) => ({
      ...prevState,
      limit: newLimit,
      currentPage: 1,
    }));
    setOffset(0);
  };
  const updateSearch = (value) => {
    if (value == null) return;
    if (value === search) return;
    const trimmedVal = String(value).trimStart();
    if (trimmedVal === search) {
      return;
    }
    setSearch(trimmedVal);
    setOffset(0);
    setState((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const categoryDialogHandler = (id = "") => {
    setState((prev) => ({
      ...prev,
      categoryDialogState: !prev.categoryDialogState,
    }));
  };

  const moveDialogHandler = () => {
    setState((prev) => ({
      ...prev,
      moveDialogState: !prev.moveDialogState,
    }));
  };

  const deleteDialogHandler = (id = "") => {
    setState((prev) => ({
      ...prev,
      deleteDialogState: !prev.deleteDialogState,
      deleteId: id,
    }));
  };

  const deleteHandler = (id) => {
    dispatch(deleteItem(id)).then((res) => {
      setState((prev) => ({
        ...prev,
        deleteDialogState: false,
      }));
      setState((prev) => ({ ...prev }));
    });
  };

  const importMaterial = () => {
    navigate(`/${CATALOGS}/${IMPORT_MATERIAL}`);
  };

  const addItem = () => {
    navigate(`/${CATALOGS}/${ADD_MATERIAL}`);
  };

  const editItem = (rowData) => {
    navigate(`/${CATALOGS}/${MATERIAL}/edit/${rowData.id}`);
  };

  const changeFilter = (categoryFilter = []) => {
    setState((prev) => ({
      ...prev,
      filter: categoryFilter,
      currentPage: 1,
    }));
    setOffset(0);
    setSearch("");
  };

  const toggleDuplicateFilter = () => {
    setDuplicateButtonLoading(true);
    setState((prev) => ({
      ...prev,
      currentPage: 1,
      showDuplicatesOnly: !prev.showDuplicatesOnly,
    }));
    setOffset(0);
    setSearch("");
  };

  const columns = [
    { title: "S No", field: "sno", filtering: false, class: "w-auto" },
    {
      title: "Item Description",
      field: "item",
      filtering: false,
      class: "w-auto",
      render: (rowData) => (
        <div className="d-flex align-items-center justify-content-between">
          <Link to={`/${CATALOGS}/${MATERIAL}/edit/${rowData.id}`}>
            {rowData.item}
          </Link>
          {rowData.duplicates?.length > 0 &&
            rowData.duplicates.some(
              (dup) => dup.hide_search === false || dup.hide_search === null
            ) && (
              <Tooltip title="Duplicate Material Found">
                <WarningAmberIcon
                  fontSize="small"
                  className="ms-2 text-warning cursor-pointer"
                  data-material-duplicate={`material-duplicate-warning-${rowData.id}`}
                  onClick={() => {
                    setSelectedMaterial(rowData);
                    setDuplicatePopupOpen(true);
                  }}
                />
              </Tooltip>
            )}
        </div>
      ),
    },
    {
      title: "Catalog",
      field: "categoryId",
      lookup: state.dynamiclookup,
      render: (rowData) => getCategoryName(rowData.categoryId),
    },
    { title: "Category", field: "manufacturer", filtering: false },
    { title: "Part No.", field: "partNo", filtering: false },
    {
      title: "Std. cost",
      field: "costValue",
      filtering: false,
      render: (rowData) =>
        rowData.costValue
          ? `${numberFormat(rowData.costValue)}/${rowData.unitLabel}`
          : "N/A",
    },
    {
      title: "Sale Lock",
      field: "saleAmountLock",
      columnSearch: true,
      render: (rowData) =>
        rowData.saleAmountLock ? (
          <span className="Lock-Icon-style">
            <LockIcon color="success"></LockIcon>
          </span>
        ) : (
          <span className="Lock-Icon-style">
            <LockOpenIcon color="warning"></LockOpenIcon>
          </span>
        ),
    },
    {
      title: "Status",
      field: "status",
      filtering: false,
      columnSearch: true,
      render: (rowData) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input switch-checkbox"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckDefault"
            checked={rowData.status === "ACTIVE" ? true : false}
            disabled={!writePermission}
            onChange={(e) => {
              dispatch(
                editItemAPI(
                  { status: e.target.checked ? "ACTIVE" : "INACTIVE" },
                  rowData.id,
                  rowData.item
                )
              ).then((data) => {
                setState((prev) => ({ ...prev }));
              });
            }}
          />
          <label
            className="form-check-label"
            htmlFor="flexSwitchCheckDefault"
          ></label>
        </div>
      ),
    },
  ];
  const mobileColumns = [
    { title: "S No", field: "sno", filtering: false, class: "w-auto" },
    { title: "Item Description", field: "item", filtering: false, class: "w-auto" },
    { title: "Catalog", field: "categoryId", render: (rowData) => getCategoryName(rowData.categoryId) },
    { title: "Manufacturer", field: "manufacturer", filtering: false },
    { title: "Part No.", field: "partNo", filtering: false },
    { title: "Std. cost", field: "costValue", filtering: false, render: (rowData) => rowData.costValue ? `${numberFormat(rowData.costValue)}/${rowData.unitLabel}` : "N/A" },
    { title: "Status", field: "status", filtering: false },
    {
      title: "",
      field: "actions",
      render: (rowData) => (
        <button
          className="btn btn-sm btn-outline-danger"
          data-cy="remove-mobile-material-btn"
          onClick={() => {
            setMaterialToRemove(rowData)
            setRemoveConfirmOpen(true)
          }}
        >
          Remove
        </button>
      )
    }
  ]
  const getCategoryName = (id) => {
    return (categoryList || []).find((item) => item.id == id)?.name || "";
  };
  const categoryOptions = (categoryList || []).map((c) => ({
    value: String(c.id),
    templateNameWithCount: c.name,
  }));

  return (
    <>
      {state.categoryDialogState && (
        <AddCategory
          open={state.categoryDialogState}
          showList={true}
          dialogClose={categoryDialogHandler}
        />
      )}
      {duplicatePopupOpen && (
        <DuplicateMaterialPopup
          material={selectedMaterial}
          onClose={() => setDuplicatePopupOpen(false)}
          onUpdateDuplicates={(updates) => {
            setDuplicatePopupOpen(false);
            setState((prev) => ({ ...prev }));
          }}
          onRefreshDuplicates={() => {
            setDuplicatePopupOpen(false);
            setSelectedMaterial(null);
            getInventoryList(state.filter, activeTab);
          }}
          onRefreshMaterialList={() => {
            getInventoryList(state.filter, activeTab);
          }}
          onRefreshItemTemplateList={() => {
            const event = new CustomEvent("refreshItemTemplateList", {
              detail: {
                timestamp: Date.now(),
                source: "DuplicateMaterialPopup",
              },
            });
            window.dispatchEvent(event);
          }}
          categoryList={categoryList}
          writePermission={writePermission}
        />
      )}

      {state.moveDialogState && (
        <MoveCatalogDialog
          open={state.moveDialogState}
          onClose={moveDialogHandler}
        />
      )}

      <DeleteDialog
        open={state.deleteDialogState}
        id={state.deleteId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogHandler}
        moduleName="Item"
      />

      <Modal
        open={removeConfirmOpen}
        onClose={() => {
          setRemoveConfirmOpen(false)
          setMaterialToRemove(null)
        }}
        fullWidth={true}
        maxWidth='sm'
        modalHeading="Remove Mobile Material"
        modalSaveTxt="Yes"
        modalCloseTxt="No"
        onClick={handleRemoveMobileMaterial}
        showActionBtn
        savebtnClass='createBtn'
        cxlbtnClass='cancelBtn'
        primary
        dataCy='remove-mobile-material-confirm-dialog'
      >
        <p>
          Are you sure you want to remove <strong>"{materialToRemove?.item}"</strong> from mobile materials?
        </p>
      </Modal>

      <div className="purchase-order-block">
        <div className="row catalog-select align-items-center">
          <div className="w-auto">
            <h2 className="content-heading text-uppercase">{searchParams.get('mode') === 'swap' ? "Swap Materials" : "Materials"}</h2>
          </div>
          {writePermission && (
            <div className="w-auto ms-auto mt-auto w-res-100 d-flex align-items-center">
              {searchParams.get('mode') !== 'swap' && (
                <>
                  <CategoryFilter
                    categories={categoryOptions}
                    selectedCategories={(state.filter || []).map(String)}
                    onCategoryChange={changeFilter}
                    label={"Catalog Filter"}
                    categoryFilterClass="material-category-filter"
                  />
                  <Tooltip
                    title={
                      state.showDuplicatesOnly
                        ? "Show All Materials"
                        : "Show Duplicate Materials Only"
                    }
                  >
                    <button
                      onClick={toggleDuplicateFilter}
                      className={`${state.showDuplicatesOnly
                        ? "btn btn-outline-warning"
                        : "secondarybtn secondarybtn-outline btn-ch"
                        } me-2`}
                      data-cy="duplicate-filter-button"
                      data-duplicate-filter-active={state.showDuplicatesOnly}
                      aria-label={
                        state.showDuplicatesOnly
                          ? "Show all materials"
                          : "Show only duplicate materials"
                      }
                      aria-pressed={state.showDuplicatesOnly}
                      disabled={duplicateButtonLoading}
                    >
                      <FilterAltIcon className="filter-alt-icon" />
                      {duplicateButtonLoading
                        ? " Loading..."
                        : state.showDuplicatesOnly
                          ? "Show All"
                          : "Duplicates"}
                    </button>
                  </Tooltip>
                </>
              )
              }
              {searchParams.get('mode') === 'swap' &&
                <button className="secondarybtn secondarybtn-outline btn-ch me-2" onClick={e => navigate('/catalogs/material')}>
                  <ArrowBackIcon className="me-2 arrow-back-icon" />
                  Materials List
                </button>
              }
              {searchParams.get('mode') !== 'swap' && <button
                onClick={() => navigate('/catalogs/material?mode=swap')}
                className="secondarybtn secondarybtn-outline btn-ch me-2 btn btn-primary"
              >
                Swap Material
              </button>}
              {(props.globalUser || writePermission) && (
                <button
                  onClick={importMaterial}
                  className="secondarybtn secondarybtn-outline btn-ch me-2 btn btn-primary"
                >
                  Import Materials
                </button>
              )}
              <button
                onClick={addItem}
                className="secondarybtn secondarybtn-outline btn-ch me-2 btn btn-primary"
              >
                Add Material
              </button>
              <button
                onClick={moveDialogHandler}
                className="secondarybtn secondarybtn-outline btn-ch me-2 btn btn-primary"
              >
                Move Items
              </button>
              <button
                onClick={categoryDialogHandler}
                className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
              >
                View Catalogs
              </button>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            {(() => {
              const isSwapMode = searchParams.get('mode') === 'swap';

              return isSwapMode ? (
                <SwapMaterial
                  onCancel={() => navigate('/catalogs/material')}
                />
              ) : (
                <>
                  {state.showDuplicatesOnly && (
                    <div className="alert alert-warning mb-3" role="alert">
                      <WarningAmberIcon className="me-2" />
                      <strong>Duplicate Filter Active:</strong> Showing only materials
                      with duplicates.
                      {state.isLoading && <span className="ms-2">Loading...</span>}
                      <br />
                    </div>
                  )}
                  <Tabs
                    activeKey={activeTab}
                    id="uncontrolled-tab-example"
                    onSelect={(k) => {
                      if (k && k !== activeTab) {
                        setActiveTab(k);
                        setOffset(0);
                        setSearch(search);
                        setState((prev) => ({
                          ...prev,
                          currentPage: 1,
                        }));
                        if (k === 'mobile') {
                          refreshMobileMaterials()
                        }
                      }
                    }}
                    className="mb-3"
                  >
                    <Tab
                      eventKey="external"
                      title={`External Materials${state.externalCount > 0 ? ` (${state.externalCount})` : ""
                        }${state.showDuplicatesOnly ? " - Duplicates" : ""}`}
                    >
                      <CustomTable
                        key={`external-${state.showDuplicatesOnly
                          }-${state.filter.join(",")}-${state.limit}`}
                        columns={columns}
                        data={state.external}
                        isEdit={writePermission}
                        editHandler={editItem}
                        fileName="External Material List"
                        paginationCount={state.externalCount}
                        pageHandler={updateOffset}
                        itemsPerPage={state.limit}
                        searchHandler={updateSearch}
                        currentPage={state.currentPage}
                        limitChangeHandler={handleLimitChange}
                        isLoading={state.isLoading}
                        externalSearchValue={search}
                        externalSearchHandler={updateSearch}
                      />
                    </Tab>
                    <Tab
                      eventKey="internal"
                      title={`Internal Materials${state.internalCount > 0 ? ` (${state.internalCount})` : ""
                        }${state.showDuplicatesOnly ? " - Duplicates" : ""}`}
                    >
                      <CustomTable
                        key={`internal-${state.showDuplicatesOnly
                          }-${state.filter.join(",")}-${state.limit}`}
                        columns={columns}
                        data={state.internal}
                        isEdit={writePermission}
                        editHandler={editItem}
                        fileName="Internal Material List"
                        paginationCount={state.internalCount}
                        pageHandler={updateOffset}
                        itemsPerPage={state.limit}
                        searchHandler={updateSearch}
                        currentPage={state.currentPage}
                        limitChangeHandler={handleLimitChange}
                        isLoading={state.isLoading}
                        externalSearchValue={search}
                        externalSearchHandler={updateSearch}
                      />
                    </Tab>
                    <Tab
                      eventKey="mobile"
                      title={`Mobile Materials${mobileSelectedIds.length ? ` (${mobileSelectedIds.length}/200)` : ''}`}
                      data-cy="mobile-materials-tab"
                    >
                      <div className="d-flex justify-content-end mb-2">
                        <button
                          onClick={() => {
                            setMobileModalOpen(true);
                          }}
                          className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                          data-cy="add-mobile-materials-btn"
                        >
                          Add Materials
                        </button>
                      </div>
                      <CustomTable
                        columns={mobileColumns}
                        data={mobileItems}
                        isEdit={false}
                        fileName="Mobile Materials"
                        paginationCount={mobileItems.length}
                        itemsPerPage={state.limit}
                        currentPage={1}
                        isLoading={mobileLoading}
                        enableSearch={false}
                      />
                    </Tab>
                  </Tabs>
                </>
              );
            })()}
          </div>
        </div>
      </div>
      {(() => {
        const seedData = [
          ...((state.external || [])),
          ...((state.internal || []))
        ]
        const seedCount = (state.externalCount || 0) + (state.internalCount || 0)
        const seedType = ''
        return (
          <MobileMaterialsModal
            open={mobileModalOpen}
            onClose={() => setMobileModalOpen(false)}
            onSaveSuccess={refreshMobileMaterials}
            initialData={seedData}
            totalCount={seedCount}
            initialParams={{
              categoryId: (state.filter || []).join(','),
              type: seedType,
              limit: state.limit,
              currentPage: state.currentPage,
              search
            }}
            initialSelectedIds={mobileSelectedIds}
          />)
      })()}
    </>
  );
}
